import { SYMPHONY_WHATSAPP_INBOUND_NUMBER } from '@wix/ai-scheduling-assistant-constants';
import { CONVERSATION_CHANNEL_READ_ONLY_ERROR_CODE } from '@wix/ai-scheduling-assistant-common-lib/entities';

// Externally-owned conversations on the web: visible, read-only. This module is the one place that
// knows which channels are "external" (owned by something other than this surface), how to recognize
// the server's typed read-only rejection, and where the "continue this conversation" CTA points.

/**
 * The externally-owned channels a conversation can live on.
 *
 * WHATSAPP/SMS are owned by a messaging thread; EXTERNAL_AGENT is owned by an external agent platform's
 * session (Gemini Enterprise first). They are read-only here for the same reason from the user's side,
 * but for different mechanics: a WhatsApp reply would be delivered into their real thread, whereas an
 * agent platform only ever sees a reply while it is waiting on a request it made — so a reply typed
 * here would go into a transcript nobody on the other side ever reads.
 */
export type ExternalChannel = 'WHATSAPP' | 'SMS' | 'EXTERNAL_AGENT';

// The errorCode the server-side channel guard stamps on its HTTP 403 body when
// a USER send targets a WhatsApp/SMS-owned conversation — re-exported from the
// shared channelOwnership contract so client and server can't drift.
export const CONVERSATION_CHANNEL_READ_ONLY = CONVERSATION_CHANNEL_READ_ONLY_ERROR_CODE;

/** Narrow a light-DTO `communicationChannel` string to an external channel, else null. */
export function externalChannelOf(channel: string | undefined): ExternalChannel | null {
  return channel === 'WHATSAPP' || channel === 'SMS' || channel === 'EXTERNAL_AGENT' ? channel : null;
}

/**
 * Recognize the channel guard's typed 403 body — `{ errorCode:
 * 'CONVERSATION_CHANNEL_READ_ONLY', channel: 'WHATSAPP'|'SMS'|'EXTERNAL_AGENT' }` — in a raw
 * response text. Returns the locked channel, or null for anything else (other
 * 403s, non-JSON bodies) so those keep their generic error path.
 */
export function parseChannelReadOnlyError(body: string): ExternalChannel | null {
  try {
    const parsed = JSON.parse(body) as { errorCode?: unknown; channel?: unknown };
    if (parsed.errorCode !== CONVERSATION_CHANNEL_READ_ONLY) return null;
    return externalChannelOf(typeof parsed.channel === 'string' ? parsed.channel : undefined);
  } catch {
    return null;
  }
}

/**
 * Where the "Continue …" CTA points, or null when there is nowhere to send the user.
 *
 * WhatsApp/SMS have ONE constant destination, so the channel alone determines the link. An agent
 * platform does not: the link is per-conversation, so it has to be passed in (read from the
 * conversation's `agentPlatformUrl` metadata, which the gateway stamps). Returning null rather than
 * falling through to some other channel's scheme is the point of this signature — an earlier version
 * took only the channel, and adding EXTERNAL_AGENT to that would have silently produced an `sms:` link.
 *
 * `agentUrl` is caller data from an external integration. It is validated as an absolute https URL
 * upstream (`isAgentPlatformUrlSafe`) and must never be rendered without that check.
 *
 * Seam for WhatsApp GROUPS: once group metadata ships (metadata.waGroupId + an invite link on the
 * conversation), a group conversation's CTA should use the group's invite link instead of the 1:1 wa.me
 * deep link. Until then a group gets the same WhatsApp CTA, without prefilled text — thread `isWaGroup`
 * (already on the light DTO) into here when the link exists.
 */
export function continueOnChannelHref(channel: ExternalChannel, agentUrl?: string | null): string | null {
  if (channel === 'WHATSAPP') return `https://wa.me/${SYMPHONY_WHATSAPP_INBOUND_NUMBER}`;
  // sms: wants the number E.164-formatted; the constant is the bare digits.
  if (channel === 'SMS') return `sms:+${SYMPHONY_WHATSAPP_INBOUND_NUMBER}`;
  return agentUrl || null;
}
